import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  BanknotesIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { ambulanceBookingService, BankTransferPaymentData } from '../../services/ambulance-booking.service';
import { api, apiRequest } from '../../services/api';

interface CompanyBankAccount {
  id: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  accountNumberLast: string;
  accountType: string;
  currency: string;
  description?: string;
  minimumAmount: number;
  displayPriority: number;
  isPrimary?: boolean;
}

interface BankTransferPaymentProps {
  bookingId: string;
  amount: number;
  onPaymentSuccess: (paymentData: any) => void;
  onPaymentError?: (error: string) => void;
  onPaymentCancel?: () => void;
  bookingData?: {
    providerName: string;
    caseNumber?: string;
    tripType: string;
    ambulanceType: string;
  };
}

const BankTransferPayment: React.FC<BankTransferPaymentProps> = ({
  bookingId,
  amount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel,
  bookingData,
}) => {
  // Validate required props
  if (!bookingId || !amount || !onPaymentSuccess) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="text-red-600">Error: Missing required payment information</div>
      </div>
    );
  }
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [companyBankAccounts, setCompanyBankAccounts] = useState<CompanyBankAccount[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [formData, setFormData] = useState<BankTransferPaymentData>({
    amount,
    bankName: '',
    accountHolderName: '',
    accountNumberLast: '',
    transferReference: '',
    transferTime: '', // Keep for API compatibility but won't be displayed
    notes: '',
    receiptImageUrl: '',
  });

  // Generate booking reference in format EKA681713
  const generateBookingReference = () => {
    const timestamp = Date.now().toString().slice(-6);
    const reference = `EKA${timestamp}`;
    setFormData(prev => ({
      ...prev,
      transferReference: reference,
    }));
    return reference;
  };

  // Fetch company bank accounts on component mount
  useEffect(() => {
    fetchCompanyBankAccounts();
    generateBookingReference();
  }, []);

  const fetchCompanyBankAccounts = async () => {
    try {
      const response = await api.get('/payments/company-bank-accounts/active/full-details');
      if (response.data) {
        const accounts = response.data.data || response.data;
        setCompanyBankAccounts(accounts.sort((a: CompanyBankAccount, b: CompanyBankAccount) =>
          b.isPrimary ? 1 : 0 - a.isPrimary ? 1 : 0 || b.displayPriority - a.displayPriority
        ));

        // Auto-select primary account or first account
        if (accounts.length > 0) {
          const primaryAccount = accounts.find((acc: CompanyBankAccount) => acc.isPrimary) || accounts[0];
          setSelectedBankId(primaryAccount.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch company bank accounts:', error);
      // Fallback to default Maybank account
      setCompanyBankAccounts([{
        id: 'fallback',
        bankName: 'Maybank Berhad',
        accountHolderName: 'Akhirat Sdn Bhd',
        accountNumber: '562810123456',
        accountNumberLast: '3456',
        accountType: 'CURRENT',
        currency: 'MYR',
        minimumAmount: 0,
        displayPriority: 1,
        isPrimary: true
      }]);
      setSelectedBankId('fallback');
    }
  };

  const getSelectedBank = () => {
    return companyBankAccounts.find(acc => acc.id === selectedBankId);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBankSelection = (bankId: string) => {
    setSelectedBankId(bankId);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      toast.error('Please upload a valid image file (JPG, PNG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    // Upload the file immediately and get the URL
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('receiptFile', file);

      // Upload the file using the API
      const response = await apiRequest.upload('/payments/upload-temp-receipt', formData);
      const receiptUrl = response.data.fileUrl || response.data.url;

      if (receiptUrl) {
        setFormData(prev => ({
          ...prev,
          receiptImageUrl: receiptUrl,
        }));
        toast.success('Receipt uploaded successfully');
      } else {
        throw new Error('No file URL received from server');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Failed to upload receipt: ' + (error.response?.data?.message || error.message));
      // Fallback: just store the file name
      const fileName = `transfer_${bookingId}_${Date.now()}.${file.type.split('/')[1]}`;
      setFormData(prev => ({
        ...prev,
        receiptImageUrl: fileName,
      }));
      toast.success('Receipt image selected (will be uploaded during payment submission)');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate form - simplified validation
      if (!formData.transferReference) {
        throw new Error('Transfer reference is required');
      }

      // Validate booking ID before submitting payment
      const bookingIdStr = String(bookingId);
      if (!bookingIdStr || bookingIdStr.trim() === '') {
        throw new Error('Invalid booking ID. Payment cannot be processed.');
      }

      // Include bank details from selected bank
      const paymentDataWithBankDetails = {
        ...formData,
        bankName: selectedBank?.bankName || '',
        accountHolderName: selectedBank?.accountHolderName || '',
        accountNumberLast: selectedBank?.accountNumberLast || '',
        transferTime: new Date().toISOString(), // Current time as fallback
      };

      // Submit payment
      const paymentResult = await ambulanceBookingService.submitPayment(bookingId, paymentDataWithBankDetails);

      toast.success('Payment submitted successfully! Awaiting admin verification.');
      onPaymentSuccess(paymentResult);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Payment submission failed';
      toast.error(errorMessage);
      // Safely call onPaymentError if it exists
      if (onPaymentError && typeof onPaymentError === 'function') {
        onPaymentError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedBank = getSelectedBank();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <BanknotesIcon className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Bank Transfer Payment</h3>
              <p className="text-sm text-gray-500">Complete your booking payment</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">RM {amount.toFixed(2)}</p>
            <p className="text-sm text-gray-500">Total Amount</p>
          </div>
        </div>

        {/* Simplified Payment Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-900 mb-3">Payment Instructions:</h4>
          <div className="text-sm text-blue-800 space-y-2">
            <div className="flex items-start">
              <span className="font-medium mr-2">1.</span>
              <span>Transfer exactly <strong>RM {amount.toFixed(2)}</strong> to the account below</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium mr-2">2.</span>
              <span>Use reference: <strong>{formData.transferReference}</strong></span>
              <button
                type="button"
                onClick={() => copyToClipboard(formData.transferReference, 'Reference number')}
                className="ml-2 text-blue-600 hover:text-blue-800"
                title="Copy reference"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-start">
              <span className="font-medium mr-2">3.</span>
              <span>Upload your receipt after transferring</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium mr-2">4.</span>
              <span>We'll verify within 2-4 hours during business hours</span>
            </div>
          </div>
        </div>

        {/* Enhanced Transfer To Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-3">Transfer to:</h4>

          {/* Bank Selection Dropdown */}
          {companyBankAccounts.length > 1 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Bank
              </label>
              <select
                value={selectedBankId}
                onChange={(e) => handleBankSelection(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                {companyBankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.bankName} {account.isPrimary ? '(Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedBank && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Bank:</p>
                <div className="flex items-center">
                  <p className="font-medium text-gray-900">{selectedBank.bankName}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-600">Account Number:</p>
                <div className="flex items-center">
                  <p className="font-medium text-gray-900">{selectedBank.accountNumber}</p>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedBank.accountNumber, 'Account number')}
                    className="ml-2 text-gray-600 hover:text-blue-600"
                    title="Copy account number"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-gray-600">Account Name:</p>
                <p className="font-medium text-gray-900">{selectedBank.accountHolderName}</p>
              </div>
              <div>
                <p className="text-gray-600">Reference:</p>
                <div className="flex items-center">
                  <p className="font-medium text-gray-900">{formData.transferReference}</p>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(formData.transferReference, 'Reference number')}
                    className="ml-2 text-gray-600 hover:text-blue-600"
                    title="Copy reference"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-gray-600">Amount:</p>
                <div className="flex items-center">
                  <p className="font-medium text-gray-900">RM {amount.toFixed(2)}</p>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(`RM ${amount.toFixed(2)}`, 'Amount')}
                    className="ml-2 text-gray-600 hover:text-blue-600"
                    title="Copy amount"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Form Toggle */}
        {!showForm ? (
          <div className="space-y-3">
            <button
              onClick={() => setShowForm(true)}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              I've Made the Transfer - Fill Payment Details
            </button>
            <button
              onClick={() => {
                if (onPaymentCancel) {
                  onPaymentCancel();
                }
              }}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-md font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel and Complete Later
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Transfer Reference - Auto-generated */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Reference *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="transferReference"
                    value={formData.transferReference}
                    onChange={handleInputChange}
                    placeholder="EKA681713"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    required
                    readOnly
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(formData.transferReference, 'Reference number')}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                    title="Copy reference"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

  
              {/* Receipt Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Receipt (Optional but recommended)
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {formData.receiptImageUrl && (
                  <p className="text-sm text-green-600 mt-1">✓ Receipt image selected</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                placeholder="Any additional information about your transfer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          </form>
        )}

        {/* Important Notes */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Payment verification typically takes 2-4 business hours</li>
                <li>Your booking will be confirmed after payment is verified</li>
                <li>Keep your transfer receipt for reference</li>
                <li>For urgent assistance, contact our support team</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankTransferPayment;